/**
 * The rules every site of a set has to meet before the set is saved.
 *
 * Coordinates are optional (PLT-5329 #8) — a site can be saved without them and geocoded from its
 * address or filled in later — but a half-filled pair (only one of latitude/longitude) is almost
 * always a mistake. A site type is required, and a weight, when present, must be >= 0 (both
 * mirroring the save endpoint's `zSiteLocationInput`).
 *
 * Weights are checked only for sign here. The all-or-nothing-per-position and at-most-100% rules
 * belong to publish, not save (see `site-weights.ts`).
 *
 * Shared by the input page and the chat card, which flag the offending cells before a save, and by
 * the `save_site_locations` chat tool, which refuses a save that breaks them.
 */

/** In the order their messages are listed. */
export const allSiteRuleViolations = ['missing-type', 'incomplete-coordinates', 'negative-weight'] as const;
export type SiteRuleViolation = (typeof allSiteRuleViolations)[number];

export const SITE_RULE_MESSAGES: Record<SiteRuleViolation, string> = {
  'missing-type': 'Every site needs a type.',
  'incomplete-coordinates': 'A site needs both latitude and longitude, or neither.',
  'negative-weight': 'Weight must be zero or greater when set.',
};

/** The fields the rules read. */
export interface SiteRuleFields {
  locationType: string | null | undefined;
  latitude?: number | null;
  longitude?: number | null;
  weight?: number | null;
}

export type SiteRuleField = 'locationType' | 'latitude' | 'longitude' | 'weight';

/** Each rule the site breaks, with the fields to flag for it. Empty when the site is valid. */
export function siteRuleViolations(
  site: SiteRuleFields,
): Array<{ violation: SiteRuleViolation; fields: SiteRuleField[] }> {
  const violations: Array<{ violation: SiteRuleViolation; fields: SiteRuleField[] }> = [];
  if (!site.locationType || site.locationType.trim() === '') {
    violations.push({ violation: 'missing-type', fields: ['locationType'] });
  }
  const hasLatitude = site.latitude != null && Number.isFinite(site.latitude);
  const hasLongitude = site.longitude != null && Number.isFinite(site.longitude);
  if (hasLatitude !== hasLongitude) {
    violations.push({ violation: 'incomplete-coordinates', fields: [hasLatitude ? 'longitude' : 'latitude'] });
  }
  if (site.weight != null && site.weight < 0) {
    violations.push({ violation: 'negative-weight', fields: ['weight'] });
  }
  return violations;
}
